<template>
  <div>
    <app-spinner v-if="!page_loaded" />
    <template v-else>
      <app-cells position="between">
        <app-cells :indent="false">
          <h1 class="title">Fund Request (ID: {{ request_data.id }})</h1>
          <div class="status-title" :style="{backgroundColor: request_data.status.color}">
            {{ request_data.status.name }}
          </div>
          <template v-if="request_data.status.code !== 'draft'">
            <div class="status-title" :style="{'backgroundColor': request_data.paid ? '#77b017' : '#e85c20'}">
              Paid: {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(request_data.amount_paid) }} {{ request_data.currency.code }}
            </div>
            <div class="status-title" :style="{'backgroundColor': request_data.efd ? '#77b017' : '#e85c20'}">
              EFD: {{ new Intl.NumberFormat('ru-RU', {minimumFractionDigits: 2}).format(request_data.amount_efd) }} {{ request_data.currency.code }}
            </div>
          </template>
        </app-cells>
        <app-button v-if="request_data.status.code === 'draft'" @click.native="onShowModalDelete(null)" theme="error" ref="delete_order">
          Delete
        </app-button>
      </app-cells>
      <div class="info-cards info-cards--nowrap">
        <div class="info-cards__card">
          <button
            v-if="request_data.status.code === 'draft'"
            class="info-cards__control"
            @click="onShowModal"
          >
            <img src="@/assets/img/edit-table-icon.svg" alt="">
          </button>
          <div class="info-cards__row">
            <div class="info-cards__row-item">
              <span>Date:</span><span>{{ dateFormat(request_data.date) }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Responsible:</span><span>{{ request_data.responsible.full_name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Approval:</span><span>{{ request_data.responsible_for_approval?.full_name || '-' }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Legal entity:</span><span>{{ request_data.legal_entity.name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Business unit:</span><span>{{ request_data.business_unit.name }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Currency:</span><span>{{ request_data.currency.name }}</span>
            </div>
          </div>
        </div>
        <div class="info-cards__card">
          <div class="info-cards__row info-cards__row-item--full">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Estimated:</span><span>{{ request_data.total_estimated_amount ? new Intl.NumberFormat('ru-RU').format(request_data.total_estimated_amount) : '-' }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Total:</span><span>{{ request_data.amount_total ? new Intl.NumberFormat('ru-RU').format(request_data.amount_total) : '-' }}</span>
            </div>
          </div>
          <div class="info-cards__row">
            <div class="info-cards__row-item info-cards__row-item--full">
              <span>Difference:</span><span>{{ request_data.amount_total ? new Intl.NumberFormat('ru-RU').format(request_data.total_difference_amount) : '-' }}</span>
            </div>
          </div>
        </div>
      </div>
      <app-cells>
        <app-textarea
          v-model="request_data.notes"
          rows="5"
          @change.native="onUpdateNotes"
          placeholder="Write a notes"
          :readonly="request_data.status.code !== 'draft'"
        />
      </app-cells>
      <app-tabs :tabs="tabs_to_show" />
      <transition name="fade" mode="out-in">
        <router-view :request-data="request_data" @changeStatus="onStatusChange" />
      </transition>
    </template>
    <modal
      name="delete-modal"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onHideDeleteModal" class="popup__close" type="button"></button>
        <div class="popup__title popup__title--flex">
          <img src="@/assets/img/warning-round-icon.svg" alt="">
          Delete request
        </div>
        <div class="popup__body">
          <div class="popup__subt">Are you sure you want to do that?</div>
          <app-cells position="center" :indent="false">
            <app-button @click.native="onHideDeleteModal" theme="dark">Cancel</app-button>
            <app-button @click.native="onDeleteOrder" ref="delete">Yes</app-button>
          </app-cells>
        </div>
      </div>
    </modal>
    <modal
      name="request-form"
      :width="400"
      :height="'auto'"
      :scrollable="true"
      :adaptive="true"
      :clickToClose="false"
      class="modal-not-overflow"
      draggable=".popup__title"
      :reset="true"
    >
      <div class="popup">
        <button @click="onCloseModal" class="popup__close" type="button"></button>
        <div class="popup__title">Edit request</div>
        <div class="popup__body">
          <form @submit.prevent="onCheckForm">
            <app-form-group label="Date" required>
              <app-date-picker
                v-model="form.date"
                placeholder="Date"
                :error="$v.form.date.$error"
              />
              <template #error>
                <div v-if="$v.form.date.$dirty && !$v.form.date.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Legal entity" required>
              <app-select
                @input="onChangeLegalEntity"
                v-model="form.legal_entity"
                :options="legal_entity_options"
                :reduce="item => item.code"
                :clearable="false"
                placeholder="Legal entity"
                :error="$v.form.legal_entity.$error"
              />
              <template #error>
                <div v-if="$v.form.legal_entity.$dirty && !$v.form.legal_entity.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Business unit" required>
              <app-select
                v-model="form.business_unit"
                :options="business_unit_options"
                :reduce="item => item.id"
                :clearable="false"
                :readonly="!form.legal_entity"
                placeholder="Business unit"
                :error="$v.form.business_unit.$error"
              />
              <template #error>
                <div v-if="$v.form.business_unit.$dirty && !$v.form.business_unit.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Responsible" required>
              <app-select
                @search="onEmployeeSearch"
                v-model="form.responsible"
                :options="responsible_options"
                :clearable="false"
                label="full_name"
                placeholder="Responsible"
                :error="$v.form.responsible.$error"
              />
              <template #error>
                <div v-if="$v.form.responsible.$dirty && !$v.form.responsible.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Responsible for approval" required>
              <app-select
                v-model="form.responsible_for_approval"
                :options="responsible_approval_options"
                :reduce="item => item.id"
                :clearable="false"
                label="full_name"
                placeholder="Responsible for approval"
                :error="$v.form.responsible_for_approval.$error"
              />
              <template #error>
                <div v-if="$v.form.responsible_for_approval.$dirty && !$v.form.responsible_for_approval.required">Required field</div>
              </template>
            </app-form-group>
            <app-form-group label="Currency" required>
              <app-select
                v-model="form.currency"
                :options="currency_options"
                :reduce="item => item.code"
                :clearable="false"
                placeholder="Currency"
                :error="$v.form.currency.$error"
              />
              <template #error>
                <div v-if="$v.form.currency.$dirty && !$v.form.currency.required">Required field</div>
              </template>
            </app-form-group>
            <app-cells position="center" :indent="false">
              <app-button :disabled="$v.form.$error" type="submit" ref="submit">Save</app-button>
            </app-cells>
          </form>
        </div>
      </div>
    </modal>
  </div>
</template>

<script>
import {
  deleteFinanceFundRequests,
  getAdministrativeBusinessUnits,
  getAdministrativeLegalEntities,
  getCurrencyList,
  getEmployeesListSearch,
  getFinanceFundRequest,
  patchFinanceFundRequests,
} from '@/http'
import { format, parseISO } from 'date-fns'
import AppTabs from '@/components/AppTabs.vue'
import { required } from 'vuelidate/lib/validators'
import debounce from 'lodash/debounce'

export default {
  name: 'FinanceFundRequestsDetailLayout',
  components: {AppTabs},
  data() {
    return {
      page_loaded: false,
      request_data: {},
      tabs_to_show: [],
      form: {},
      legal_entity_options: [],
      business_unit_options: [],
      responsible_options: [],
      currency_options: [],
      responsible_approval_options: [],
      tabs: [
        { name: 'finance-fund-requests-detail-items', label: 'Items', statuses: [] },
        // { name: 'finance-fund-requests-detail-invoices', label: 'Payment requests', statuses: ['completed', 'approved', 'requested', 'rejected'] },
        { name: 'finance-fund-requests-detail-cashflow', label: 'Cashflow', statuses: ['completed', 'approved', 'requested', 'rejected'] },
        { name: 'finance-fund-requests-detail-receipts', label: 'EFD receipts', statuses: ['completed', 'approved', 'requested', 'rejected'] },
      ],
    }
  },
  validations: {
    form: {
      legal_entity: { required },
      business_unit: { required },
      currency: { required },
      date: { required },
      responsible: { required },
      responsible_for_approval: { required },
    },
  },
  created() {
    getEmployeesListSearch({ fund_approvals: true }).then(response => { this.responsible_approval_options = response.data })
    getCurrencyList({ local: true }).then(response => { this.currency_options = response.data })
    getAdministrativeLegalEntities().then(response => { this.legal_entity_options = response.data })
    this.fetchData()
      .then(() => {
        getAdministrativeBusinessUnits({ legal_entity: this.request_data.legal_entity.code })
          .then(response => {
            this.business_unit_options = response.data
          })
      })
  },
  methods: {
    async fetchData() {
      const response = await getFinanceFundRequest(Number(this.$route.params.id))
      this.request_data = response.data
      this.tabsToShow()
      this.page_loaded = true
    },
    onChangeLegalEntity() {
      this.form.business_unit = null
      this.business_unit_options = []
      getAdministrativeBusinessUnits({ legal_entity: this.form.legal_entity })
        .then(response => {
          this.business_unit_options = response.data
        })
    },
    onShowModal() {
      this.form = {
        business_unit: this.request_data.business_unit.id,
        currency: this.request_data.currency.code,
        date: format(parseISO(this.request_data.date), 'yyyy-MM-dd'),
        legal_entity: this.request_data.legal_entity.code,
        responsible: this.request_data.responsible,
        responsible_for_approval: this.request_data.responsible_for_approval?.id,
      }
      this.$v.form.$reset()
      this.$modal.show('request-form')
    },
    onCloseModal() {
      this.$modal.hide('request-form')
    },
    onCheckForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        this.$notify({
          type: 'info',
          title: 'Check that the form fields are filled in correctly',
        })
      } else {
        this.sendForm()
      }
    },
    sendForm() {
      this.$refs.submit.preload = true
      const data = {...this.form, responsible: this.form.responsible.id}
      patchFinanceFundRequests(Number(this.$route.params.id), data)
        .then(() => {
          this.fetchData()
          this.$notify({
            type: 'success',
            title: 'Changes saved',
          })
          this.onCloseModal()
        })
        .catch(() => { this.$refs.submit.preload = false })
    },
    tabsToShow() {
      const tabs = []
      this.tabs.forEach(item => {
        if (!item.statuses.length) {
          tabs.push(item)
          return
        }
        if (item.statuses.some(status => status === this.request_data.status.code)) tabs.push(item)
      })
      this.tabs_to_show = tabs
    },
    onStatusChange() {
      this.fetchData()
    },
    onShowModalDelete() {
      this.$modal.show('delete-modal')
    },
    onHideDeleteModal() {
      this.$modal.hide('delete-modal')
    },
    onDeleteOrder() {
      this.$refs.delete.preload = true
      deleteFinanceFundRequests(Number(this.$route.params.id))
        .then(() => {
          this.$notify({
            type: 'success',
            title: 'Request deleted',
          })
          this.$router.push({ name: 'finance-fund-requests-active' })
        })
        .catch(() => { this.$refs.delete.preload = false })
    },
    onUpdateNotes() {
      patchFinanceFundRequests(Number(this.$route.params.id), { notes: this.request_data.notes })
        .then(() => {
          this.fetchData()
          this.$notify({
            type: 'success',
            title: 'Notes updated',
          })
        })
    },
    dateFormat(date) {
      return date ? format(parseISO(date), 'dd MMM yyyy') : '-'
    },
    onEmployeeSearch(search, loading) {
      loading(true)
      this.searchEmployee(loading, search, this)
    },
    searchEmployee: debounce((loading, search, vm) => {
      if (!search) {
        loading(false)
        vm.responsible_options = []
      } else {
        getEmployeesListSearch({search}).then(response => {
          vm.responsible_options = response.data
          loading(false)
        })
      }
    }, 350),
  }
}
</script>
